import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  Typography,
  LinearProgress,
  Box,
  InputAdornment,
  IconButton,
  Checkbox,
  MenuItem,
  Link as MuiLink,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { animate, stagger } from "motion";
import getApiBase from "../components/getApiBase";
import PrivacyPolicy from "../assets/Autoshops_Privacy_Policy.pdf";
import TermsOfService from "../assets/Autoshops_Terms_and_Conditions.pdf";
import CarDropdowns from "./CarDropdown";
import TurnstileWidget from "./TurnstileWidget";
import { usStates } from "../data/states";
import { Link as RouterLink } from "react-router-dom";

const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const UserSignupForm = ({
  onSuccess,
  onError,
  onFormStart,
  onFormBackToStart,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    vin: "",
    mileage: "",
    carMake: "",
    carModel: "",
    carYear: "",
    carTrim: "",
    engineSize: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    role: "User",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Field validation states
  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldTouched, setFieldTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  
  // Step state for multi-step form
  const [step, setStep] = useState(0);

  const handleTurnstileVerify = (token) => {
    setTurnstileToken(token);
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const evaluatePassword = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[!@#$%^&*()_\-+=]/.test(password)) strength += 25; // Fixed: Match backend exactly
    if (/[A-Z]/.test(password)) strength += 25;
    return strength;
  };

  // Comprehensive field validation
  const validateField = (name, value) => {
    const errors = {};
    
    switch (name) {
      case 'firstName':
      case 'lastName':
        if (!value.trim()) {
          errors[name] = `${name === 'firstName' ? 'First' : 'Last'} name is required`;
        } else if (value.length > 50) {
          errors[name] = `${name === 'firstName' ? 'First' : 'Last'} name must be 50 characters or less`;
        } else if (!/^[a-zA-Z\s'-]+$/.test(value)) {
          errors[name] = `${name === 'firstName' ? 'First' : 'Last'} name can only contain letters, spaces, hyphens, and apostrophes`;
        }
        break;
        
      case 'email':
        if (!value.trim()) {
          errors[name] = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors[name] = 'Please enter a valid email address';
        }
        break;
        
      case 'phone':
        if (!value.trim()) {
          errors[name] = 'Phone number is required';
        } else if (!/^\+?1?\d{10,14}$/.test(value.replace(/[\s\-\(\)]/g, ''))) {
          errors[name] = 'Please enter a valid phone number';
        }
        break;
        
      case 'password':
        const passwordErrors = [];
        if (value.length < 8) passwordErrors.push('at least 8 characters');
        if (!/[0-9]/.test(value)) passwordErrors.push('one number');
        if (!/[A-Z]/.test(value)) passwordErrors.push('one uppercase letter');
        if (!/[!@#$%^&*()_\-+=]/.test(value)) passwordErrors.push('one special character (! @ # $ % ^ & * ( ) _ - + =)');
        
        if (passwordErrors.length > 0) {
          errors[name] = `Password must contain: ${passwordErrors.join(', ')}`;
        }
        break;
        
      case 'streetAddress':
        if (!value.trim()) {
          errors[name] = 'Street address is required';
        } else if (!/^[a-zA-Z0-9\s#\-.,]+$/.test(value)) {
          errors[name] = 'Steet address contains invalid characters';
        }
        break;
        
      case 'city':
        if (!value.trim()) {
          errors[name] = 'City is required';
        } else if (!/^[a-zA-Z\s-]+$/.test(value)) {
          errors[name] = 'City can only contain letters, spaces, and hyphens'
        }
        break;
        
      case 'state':
        if (!value.trim()) {
          errors[name] = 'State is required';
        }
        break;
        
      case 'zipCode':
        if (!value.trim()) {
          errors[name] = 'ZIP code is required';
        } else if (!/^\d{5}(-\d{4})?$/.test(value)) {
          errors[name] = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
        }
        break;
        
      default:
        break;
    }
    
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "password") setPasswordStrength(evaluatePassword(value));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Helper function to format phone number
  const formatPhoneNumber = (phoneNumber) => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Check if it's a valid US phone number length
    if (cleaned.length === 10) {
      // Format as (XXX) XXX-XXXX
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      // Format as +1 (XXX) XXX-XXXX
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    
    // Return original if not a standard format
    return phoneNumber;
  };

  const handleBlur = async (e) => {
    const { name, value } = e.target;
    setFieldTouched(prev => ({ ...prev, [name]: true }));
    
    // Auto-format phone number
    if (name === 'phone' && value.trim()) {
      const formattedPhone = formatPhoneNumber(value);
      if (formattedPhone !== value) {
        setFormData(prev => ({ ...prev, phone: formattedPhone }));
      }
    }
    
    // Check email availability when user leaves email field
    if (name === 'email' && value.trim()) {
      const normalizedEmail = value.toLowerCase().trim();
      
      // Only check if email format is valid
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
        try {
          const response = await fetch(`${getApiBase()}/api/auth/check-email?email=${encodeURIComponent(normalizedEmail)}`, {
            method: "GET",
            credentials: "include",
          });

          const data = await response.json();

          if (!data.available) {
            // Email is already registered
            setFieldErrors(prev => ({
              ...prev,
              email: data.message || "This email is already registered"
            }));
          } else {
            // Email is available - clear any previous email error
            setFieldErrors(prev => {
              const newErrors = { ...prev };
              if (newErrors.email && newErrors.email.includes("already registered")) {
                delete newErrors.email;
              }
              return newErrors;
            });
          }
        } catch (error) {
          console.error("Error checking email availability:", error);
          // Don't block form submission if check fails - backend will catch it
        }
      }
    }
    
    const fieldValidationErrors = validateField(name, value);
    setFieldErrors(prev => ({ ...prev, ...fieldValidationErrors }));
  };

  // Validate car fields
  const validateCarFields = () => {
    // If any car field is filled, all must be valid
    if (formData.carMake || formData.carModel || formData.carYear) {
      // Year validation
      if (!formData.carYear || !formData.carYear.trim()) {
        return false;
      }
      const yearNum = parseInt(formData.carYear);
      const currentYear = new Date().getFullYear();
      const maxYear = currentYear + 1;
      if (isNaN(yearNum) || yearNum < 1900 || yearNum > maxYear || formData.carYear.length !== 4) {
        return false;
      }
      
      // Make and Model are required if car info is provided
      if (!formData.carMake || !formData.carMake.trim()) {
        return false;
      }
      if (!formData.carModel || !formData.carModel.trim()) {
        return false;
      }
    }
    return true;
  };

  // Check if form is valid
  const checkFormValidity = () => {
    // Step-based validation for multi-step form
    if (step === 0) {
      // Step 0: Personal info only
      const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'password'];
      const hasAllRequiredFields = requiredFields.every(field => formData[field]?.trim());
      const hasNoErrors = Object.keys(fieldErrors).length === 0;
      const hasStrongPassword = passwordStrength === 100;
      
      return hasAllRequiredFields && hasNoErrors && hasStrongPassword;
    } else if (step === 1) {
      // Step 1: Address info
      const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'password', 'streetAddress', 'city', 'state', 'zipCode'];
      const hasAllRequiredFields = requiredFields.every(field => formData[field]?.trim());
      const hasNoErrors = Object.keys(fieldErrors).length === 0;
      const hasStrongPassword = passwordStrength === 100;
      
      return hasAllRequiredFields && hasNoErrors && hasStrongPassword;
    }
    
    // Final step: all fields, terms, and Turnstile
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'password', 'streetAddress', 'city', 'state', 'zipCode'];
    const hasAllRequiredFields = requiredFields.every(field => formData[field]?.trim());
    const hasNoErrors = Object.keys(fieldErrors).length === 0;
    const hasStrongPassword = passwordStrength === 100;
    const hasValidCarFields = validateCarFields();
    const hasTurnstile = !turnstileSiteKey || Boolean(turnstileToken);
    
    return hasAllRequiredFields && hasNoErrors && hasStrongPassword && hasValidCarFields && agreedToTerms && hasTurnstile;
  };

  // Update form validity when dependencies change (including car fields)
  React.useEffect(() => {
    setIsFormValid(checkFormValidity());
  }, [formData, fieldErrors, passwordStrength, agreedToTerms, step, formData.carYear, formData.carMake, formData.carModel, turnstileToken]);

  const handleVinSubmit = async () => {
    if (!formData.vin) return;
    const res = await fetch(
      `https://vpic.nhtsa.dot.gov/api/vehicles/decodevinvaluesextended/${formData.vin}?format=json`
    );
    const data = await res.json();
    if (data?.Results?.length > 0) {
      const vehicle = data.Results[0];
      setFormData((prev) => ({
        ...prev,
        carMake: vehicle.Make,
        carModel: vehicle.Model,
        carYear: vehicle.ModelYear,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (passwordStrength < 100) {
      onError("Password must meet all requirements!", "error");
      return;
    }

    // Validate car year if car information is provided
    if (formData.carMake || formData.carModel || formData.carYear) {
      if (!formData.carYear || !formData.carYear.trim()) {
        onError("Car year is required when providing car information.", "error");
        return;
      }
      
      const yearNum = parseInt(formData.carYear);
      const currentYear = new Date().getFullYear();
      const maxYear = currentYear + 1; // Allow up to 1 year in the future
      
      // Validate year is a number and within reasonable range (1900 to current year + 1)
      if (isNaN(yearNum) || yearNum < 1900 || yearNum > maxYear) {
        onError(`Please enter a valid car year between 1900 and ${maxYear}.`, "error");
        return;
      }
      
      // Additional validation: year should be 4 digits
      if (formData.carYear.length !== 4) {
        onError("Car year must be a 4-digit year (e.g., 2020).", "error");
        return;
      }
    }

    if (turnstileSiteKey && !turnstileToken) {
      onError("Please complete the bot verification challenge.", "error");
      return;
    }

    try {
      const response = await fetch(`${getApiBase()}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...formData, turnstileToken }),
      });

      const data = await response.json();
      if (response.ok) {
        // Check if email verification is required (Users now require verification like Mechanics/Owners)
        if (data.requiresEmailVerification) {
          // Don't store token or user - they need to verify email first
          onSuccess({
            requiresEmailVerification: true,
            email: data.email,
            message: data.message || "Account created! Please check your email to verify your account before logging in."
          });
        } else {
          // Auto-login for other cases (shouldn't happen for Users now, but kept for safety)
          if (data.token) {
            localStorage.setItem('token', data.token);
          }
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
          }
          onSuccess(data.user); // Pass user data to show welcome message
        }
      }
      else {
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        onError(data.error || "Signup failed!", "error");
      }
    } catch (err) {
      console.error("❌ Signup error:", err);
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      onError("Server error! Try again.", "error");
    }
  };

  // FUNCTIONS TO CONTROL NEXT/BACK BUTTONS
  function next() {
    if (step === 0 && onFormStart) {
      onFormStart();
    }
    setStep(step + 1);
  }

  function prev() {
    const newStep = step - 1;
    if (newStep === 0 && onFormBackToStart) {
      onFormBackToStart();
    }
    setStep(newStep);
  }

  // ANIMATIONS
  useEffect(() => {
    const userInputs = Array.from(document.getElementsByClassName("userInput"));
    if (userInputs.length > 0) {
      animate(
        userInputs,
        { x: [-30, 0], opacity: [0, 1] },
        { delay: stagger(0.3) }
      );
    }
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [step]);

  const inputArray = [
    <>
      <TextField
        className="userInput"
        label="First Name"
        name="firstName"
        fullWidth
        required
        value={formData.firstName}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.firstName && !!fieldErrors.firstName}
        helperText={fieldTouched.firstName && fieldErrors.firstName}
        sx={{ mb: 2 }}
      />
      <TextField
        className="userInput"
        label="Last Name"
        name="lastName"
        fullWidth
        required
        value={formData.lastName}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.lastName && !!fieldErrors.lastName}
        helperText={fieldTouched.lastName && fieldErrors.lastName}
        sx={{ mb: 2 }}
      />
      <TextField
        className="userInput"
        label="Email"
        name="email"
        type="email"
        fullWidth
        required
        value={formData.email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.email && !!fieldErrors.email}
        helperText={fieldTouched.email && fieldErrors.email}
        sx={{ mb: 2 }}
      />
      <TextField
        className="userInput"
        label="Phone Number"
        name="phone"
        type="tel"
        fullWidth
        required
        value={formData.phone}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.phone && !!fieldErrors.phone}
        helperText={fieldTouched.phone && fieldErrors.phone}
        sx={{ mb: 2 }}
      />
      <TextField
        className="userInput"
        label="Password"
        name="password"
        type={showPassword ? "text" : "password"}
        value={formData.password}
        onChange={handleChange}
        onBlur={handleBlur}
        fullWidth
        required
        error={fieldTouched.password && !!fieldErrors.password}
        helperText={fieldTouched.password && fieldErrors.password}
        sx={{ mb: 1 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={togglePasswordVisibility} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ mb: 1 }}>
        <LinearProgress
          variant="determinate"
          value={passwordStrength}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: "#f0f0f0",
            "& .MuiLinearProgress-bar": {
              backgroundColor:
                passwordStrength < 50
                  ? "#f44336"
                  : passwordStrength < 75
                  ? "#ff9800"
                  : "#4caf50",
            },
          }}
        />
      </Box>

      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
      >
        Password must:
        <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
          <li
            style={{ color: formData.password.length >= 8 ? "green" : "red" }}
          >
            Be at least 8 characters
          </li>
          <li
            style={{ color: /[0-9]/.test(formData.password) ? "green" : "red" }}
          >
            Contain a number
          </li>
          <li
            style={{
              color: /[!@#$%^&*()_\-+=]/.test(formData.password)
                ? "green"
                : "red",
            }}
          >
            Contain a special character: ! @ # $ % ^ & * ( ) _ - + =
          </li>
          <li
            style={{ color: /[A-Z]/.test(formData.password) ? "green" : "red" }}
          >
            Contain an uppercase letter
          </li>
        </ul>
      </Typography>
    </>,
    <>
      <Typography variant="h6" mt={3}>
        Address
      </Typography>
      <TextField
        className="userInput"
        label="Street Address"
        name="streetAddress"
        fullWidth
        required
        value={formData.streetAddress}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.streetAddress && !!fieldErrors.streetAddress}
        helperText={fieldTouched.streetAddress && fieldErrors.streetAddress}
        sx={{ mt: 2 }}
      />
      <TextField
        className="userInput"
        label="City"
        name="city"
        fullWidth
        required
        value={formData.city}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.city && !!fieldErrors.city}
        helperText={fieldTouched.city && fieldErrors.city}
        sx={{ mt: 2 }}
      />
      <TextField
        className="userInput"
        select
        label="State"
        name="state"
        fullWidth
        required
        value={formData.state}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.state && !!fieldErrors.state}
        helperText={fieldTouched.state && fieldErrors.state}
        sx={{ mt: 2 }}
      >
        {usStates.map(({ code, name }) => (
          <MenuItem key={code} value={code}>
            {name} ({code})
          </MenuItem>
        ))}
      </TextField>

      <TextField
        className="userInput"
        label="Zip Code"
        name="zipCode"
        fullWidth
        required
        value={formData.zipCode}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.zipCode && !!fieldErrors.zipCode}
        helperText={fieldTouched.zipCode && fieldErrors.zipCode}
        sx={{ mt: 2 }}
      />
    </>,
    <>
      <Typography variant="h6" mt={3}>
        Vehicle Info
      </Typography>

      <TextField
        className="userInput"
        label="VIN (optional)"
        name="vin"
        fullWidth
        value={formData.vin}
        onChange={handleChange}
        sx={{ mt: 2 }}
      />
      <Button
        className="userInput"
        variant="outlined"
        onClick={handleVinSubmit}
        sx={{ mt: 2 }}
      >
        Decode VIN
      </Button>

      <CarDropdowns formData={formData} handleChange={handleChange} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 1,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            component={RouterLink}
            to="/privacypolicy"
            target="_blank"
            variant="contained"
            sx={{ my: 1, textAlign: "center" }}
          >
            Privacy Policy
          </Button>
          <Button
            component={RouterLink}
            to="/tos"
            variant="contained"
            target="_blank"
            sx={{ my: 1, textAlign: "center" }}
          >
            Terms of Service
          </Button>
        </Box>
        <Typography
          variant="body"
          sx={{ my: 2, maxWidth: 300, textAlign: "center" }}
        >
          <Checkbox 
            required 
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
          />
          By ticking this box I agree that I have read the privacy policy and
          terms of service.
        </Typography>
        <TurnstileWidget
          ref={turnstileRef}
          onVerify={handleTurnstileVerify}
          onExpire={handleTurnstileExpire}
          onError={() => setTurnstileToken(null)}
        />
      </Box>
    </>,
  ];

  return (
    <>
      <Typography
        variant="h5"
        mb={3}
        fontWeight={800}
        borderBottom={3}
        textAlign="center"
      >
        Car Owner Sign up
      </Typography>
      <form onSubmit={handleSubmit}>
        {inputArray[step]}
        <Box
          fullWidth
          sx={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            minHeight: "80px",
          }}
        >
          {step < inputArray.length - 1 && (
            <Button
              variant="contained"
              onClick={next}
              disabled={!isFormValid}
              sx={{ position: "absolute", right: 0 }}
            >
              Next
            </Button>
          )}

          {step > 0 && (
            <Button
              variant="contained"
              onClick={prev}
              sx={{ position: "absolute", left: 0 }}
            >
              Back
            </Button>
          )}

          {step === inputArray.length - 1 && (
            <Button
              type="submit"
              variant="contained"
              disabled={!isFormValid}
              sx={{ position: "absolute", right: 0 }}
            >
              Sign Up
            </Button>
          )}
        </Box>
      </form>
    </>
  );
};

export default UserSignupForm;
